import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const VehicleInspections = ({ onBack, currentUser }) => {
  const [fleetTrucks, setFleetTrucks] = useState([]);
  const [fleetTrailers, setFleetTrailers] = useState([]);
  const [formData, setFormData] = useState({
    inspector_name: '',
    inspection_date: new Date().toISOString().split('T')[0],
    inspection_time: new Date().toTimeString().slice(0, 5),
    // Fleet Integration
    truck_selection: '',
    truck_id: '',
    trailer_selection: '',
    trailer_id: '',
    // Form Fields
    truck_unit_number: '',
    trailer_unit_number: '',
    license_plate: '',
    odometer_reading: '',
    has_trailer: false,
    // Pickup Truck Inspection Items (Ontario Regulation 199/07 Schedule 1)
    truck_inspection_items: {
      "Cab – Doors open/close securely": false,
      "Driver Controls – Pedals, indicators, dashboard lights functional": false,
      "Driver Seat & Seatbelt – Functional and in good condition": false,
      "Hydraulic Brakes – No leaks, parking brake holds": false,
      "Lamps & Reflectors – All exterior lights operational": false,
      "Steering – No excessive play, smooth control": false,
      "Suspension – Springs/shocks intact, no sagging": false,
      "Tires – No visible damage, correct inflation": false,
      "Wheels, Hubs, Fasteners – No loose or missing lug nuts, no cracks": false,
      "Exhaust System – No leaks, no fumes in cab": false,
      "Fuel System – Cap secured, no visible leaks": false,
      "Frame & Body – No cracks, deformation or shifting": false,
      "Glass & Mirrors – No cracks, clear visibility": false,
      "Heater & Defroster – Works properly": false,
      "Horn – Operational": false,
      "Windshield Wipers & Washer – Blades effective, fluid sprays": false,
      "Emergency Equipment – Fire extinguisher, safety triangles present": false,
      "Load Secure – Any loads are properly secured down": false
    },
    // Trailer Inspection Items (Ontario Regulation 199/07 Schedule 1)
    trailer_inspection_items: {
      "Hitch & Coupler – Coupler locked, pin/lock secured, safety chains crossed and fastened": false,
      "Frame – No visible structural damage or cracks": false,
      "Lamps & Reflectors – Brake, turn and marker lights functional": false,
      "Suspension – No broken springs or axle issues": false,
      "Tires – No cuts or bulges, good tread, proper inflation": false,
      "Wheels, Hubs, Fasteners – No oil leaks, lug nuts tight and intact": false,
      "Load Secure – Any loads are properly secured down": false
    },
    major_deficiency_comments: '',
    has_major_deficiencies: false,
    inspector_signature: '',
    inspector_email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchFleetData();
  }, []);

  const fetchFleetData = async () => {
    try {
      // Fetch trucks
      const trucksResponse = await axios.get(`${API}/fleet/Truck`);
      setFleetTrucks(trucksResponse.data);
      
      // Fetch trailers  
      const trailersResponse = await axios.get(`${API}/fleet/Trailer`);
      setFleetTrailers(trailersResponse.data);
    } catch (error) {
      console.error('Error fetching fleet data:', error);
      // Initialize default fleet if none exists
      try {
        await axios.post(`${API}/fleet/initialize`);
        const trucksResponse = await axios.get(`${API}/fleet/Truck`);
        const trailersResponse = await axios.get(`${API}/fleet/Trailer`);
        setFleetTrucks(trucksResponse.data);
        setFleetTrailers(trailersResponse.data);
      } catch (initError) {
        console.error('Error initializing fleet:', initError);
      }
    }
  };

  const handleTruckChange = (truckId) => {
    const selectedTruck = fleetTrucks.find(truck => truck.id === truckId);
    if (selectedTruck) {
      setFormData(prev => ({
        ...prev,
        truck_selection: `${selectedTruck.unit_number} - ${selectedTruck.year} ${selectedTruck.make} ${selectedTruck.model}`,
        truck_id: selectedTruck.id,
        truck_unit_number: selectedTruck.unit_number,
        license_plate: selectedTruck.license_plate || ''
      }));
    }
  };

  const handleTrailerChange = (trailerId) => {
    const selectedTrailer = fleetTrailers.find(trailer => trailer.id === trailerId);
    if (selectedTrailer) {
      setFormData(prev => ({
        ...prev,
        trailer_selection: `${selectedTrailer.unit_number} - ${selectedTrailer.year} ${selectedTrailer.make} ${selectedTrailer.model}`,
        trailer_id: selectedTrailer.id,
        trailer_unit_number: selectedTrailer.unit_number
      }));
    }
  };

  const handleTruckInspectionChange = (item, checked) => {
    setFormData(prev => ({
      ...prev,
      truck_inspection_items: {
        ...prev.truck_inspection_items,
        [item]: checked
      }
    }));
  };

  const handleTrailerInspectionChange = (item, checked) => {
    setFormData(prev => ({
      ...prev,
      trailer_inspection_items: {
        ...prev.trailer_inspection_items,
        [item]: checked
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Check if any inspection items failed
    const truckFailures = Object.entries(formData.truck_inspection_items).filter(([item, status]) => !status);
    const trailerFailures = formData.has_trailer ? 
      Object.entries(formData.trailer_inspection_items).filter(([item, status]) => !status) : [];
    
    const hasDeficiencies = truckFailures.length > 0 || trailerFailures.length > 0 || 
                           formData.major_deficiency_comments.trim() !== '';
    
    try {
      const response = await axios.post(`${API}/vehicle-inspections`, {
        ...formData,
        has_major_deficiencies: hasDeficiencies
      });
      
      const result = response.data;
      let message = `Vehicle inspection submitted successfully!\n\nInspection report sent to: ${formData.inspector_email}`;
      
      if (result.deficiencies_found) {
        message += `\n\n⚠️ ALERT: ${result.failed_items} deficiencies found!`;
        message += `\nDeficiency alert sent to ${currentUser?.company?.email || 'the office'} AND your email`;
        message += '\nVehicle should be removed from service until repairs are completed.';
      }
      
      alert(message);
      
      // Reset form
      setFormData({
        inspector_name: '',
        inspection_date: new Date().toISOString().split('T')[0],
        inspection_time: new Date().toTimeString().slice(0, 5),
        truck_selection: '',
        truck_id: '',
        trailer_selection: '',
        trailer_id: '',
        truck_unit_number: '',
        trailer_unit_number: '',
        license_plate: '',
        odometer_reading: '',
        has_trailer: false,
        truck_inspection_items: {
          "Cab – Doors open/close securely": false,
          "Driver Controls – Pedals, indicators, dashboard lights functional": false,
          "Driver Seat & Seatbelt – Functional and in good condition": false,
          "Hydraulic Brakes – No leaks, parking brake holds": false,
          "Lamps & Reflectors – All exterior lights operational": false,
          "Steering – No excessive play, smooth control": false,
          "Suspension – Springs/shocks intact, no sagging": false,
          "Tires – No visible damage, correct inflation": false,
          "Wheels, Hubs, Fasteners – No loose or missing lug nuts, no cracks": false,
          "Exhaust System – No leaks, no fumes in cab": false,
          "Fuel System – Cap secured, no visible leaks": false,
          "Frame & Body – No cracks, deformation or shifting": false,
          "Glass & Mirrors – No cracks, clear visibility": false,
          "Heater & Defroster – Works properly": false,
          "Horn – Operational": false,
          "Windshield Wipers & Washer – Blades effective, fluid sprays": false,
          "Emergency Equipment – Fire extinguisher, safety triangles present": false,
          "Load Secure – Any loads are properly secured down": false
        },
        trailer_inspection_items: {
          "Hitch & Coupler – Coupler locked, pin/lock secured, safety chains crossed and fastened": false,
          "Frame – No visible structural damage or cracks": false,
          "Lamps & Reflectors – Brake, turn and marker lights functional": false,
          "Suspension – No broken springs or axle issues": false,
          "Tires – No cuts or bulges, good tread, proper inflation": false,
          "Wheels, Hubs, Fasteners – No oil leaks, lug nuts tight and intact": false,
          "Load Secure – Any loads are properly secured down": false
        },
        major_deficiency_comments: '',
        has_major_deficiencies: false,
        inspector_signature: '',
        inspector_email: ''
      });
    } catch (error) {
      console.error('Error submitting inspection:', error);
      alert('Error submitting inspection. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">DAILY VEHICLE & TRAILER INSPECTION</h1>
          <div></div>
        </div>

        {/* Compliance Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 text-center">
          <p className="text-blue-800 font-medium">
            ⚖️ Ontario Regulation 199/07 (Schedule 1) Compliant
          </p>
          <p className="text-blue-600 text-sm">Pickup trucks and tag-along trailers without air brakes</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Inspector Information */}
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Inspector Information</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Inspector Name"
                value={formData.inspector_name}
                onChange={(e) => setFormData(prev => ({ ...prev, inspector_name: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <input
                type="email"
                placeholder="Inspector Email"
                value={formData.inspector_email}
                onChange={(e) => setFormData(prev => ({ ...prev, inspector_email: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <input
                type="date"
                value={formData.inspection_date}
                onChange={(e) => setFormData(prev => ({ ...prev, inspection_date: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <input
                type="time"
                value={formData.inspection_time}
                onChange={(e) => setFormData(prev => ({ ...prev, inspection_time: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* Vehicle Information */}
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Vehicle Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Fleet Truck Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Truck from Fleet</label>
                <select
                  value={formData.truck_id}
                  onChange={(e) => handleTruckChange(e.target.value)}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Truck from Fleet</option>
                  {fleetTrucks.map((truck) => (
                    <option key={truck.id} value={truck.id}>
                      {truck.unit_number} - {truck.year} {truck.make} {truck.model}
                    </option>
                  ))}
                </select>
              </div>

              {/* Auto-populated Unit Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Truck Unit Number</label>
                <input
                  type="text"
                  value={formData.truck_unit_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, truck_unit_number: e.target.value.toUpperCase() }))}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Auto-filled from fleet selection"
                  required
                />
              </div>

              {/* License Plate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">License Plate</label>
                <input
                  type="text"
                  value={formData.license_plate}
                  onChange={(e) => setFormData(prev => ({ ...prev, license_plate: e.target.value.toUpperCase() }))}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Auto-filled from fleet selection"
                  required
                />
              </div>

              {/* Odometer Reading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Odometer Reading</label>
                <input
                  type="number"
                  value={formData.odometer_reading}
                  onChange={(e) => setFormData(prev => ({ ...prev, odometer_reading: e.target.value }))}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Current odometer reading"
                  required
                />
              </div>

              {/* Trailer Option */}
              <div className="md:col-span-2">
                <label className="flex items-center space-x-3 cursor-pointer p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.has_trailer}
                    onChange={(e) => setFormData(prev => ({ ...prev, has_trailer: e.target.checked }))}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-800 font-medium">This vehicle is pulling a trailer</span>
                </label>
              </div>
            </div>
            
            {/* Trailer Selection */}
            {formData.has_trailer && (
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Trailer from Fleet</label>
                  <select
                    value={formData.trailer_id}
                    onChange={(e) => handleTrailerChange(e.target.value)}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Trailer from Fleet</option>
                    {fleetTrailers.map((trailer) => (
                      <option key={trailer.id} value={trailer.id}>
                        {trailer.unit_number} - {trailer.year} {trailer.make} {trailer.model}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trailer Unit Number</label>
                  <input
                    type="text"
                    value={formData.trailer_unit_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, trailer_unit_number: e.target.value.toUpperCase() }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Auto-filled from fleet selection"
                    required
                  />
                </div>
              </div>
            )}
          </div>

          {/* Pickup Truck Inspection Items */}
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Pickup Truck Inspection Items</h2>
            <p className="text-gray-600 text-sm mb-6">Check each item that PASSES inspection. Leave unchecked if deficient.</p>
            <div className="space-y-3">
              {Object.entries(formData.truck_inspection_items).map(([item, checked]) => (
                <label key={item} className="flex items-start space-x-3 cursor-pointer p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => handleTruckInspectionChange(item, e.target.checked)}
                    className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0 rounded focus:ring-green-500"
                  />
                  <span className="text-gray-800 font-medium leading-relaxed">{item}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Trailer Inspection Items */}
          {formData.has_trailer && (
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Trailer Inspection Items</h2>
              <p className="text-gray-600 text-sm mb-6">Check each item that PASSES inspection. Leave unchecked if deficient.</p>
              <div className="space-y-3">
                {Object.entries(formData.trailer_inspection_items).map(([item, checked]) => (
                  <label key={item} className="flex items-start space-x-3 cursor-pointer p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => handleTrailerInspectionChange(item, e.target.checked)}
                      className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0 rounded focus:ring-green-500"
                    />
                    <span className="text-gray-800 font-medium leading-relaxed">{item}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Major Deficiency Comments */}
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Major Deficiency Comments</h2>
            <p className="text-gray-600 text-sm mb-4">If any major deficiencies are identified, provide details below:</p>
            <textarea
              placeholder="Describe any major deficiencies found during inspection..."
              value={formData.major_deficiency_comments}
              onChange={(e) => setFormData(prev => ({ ...prev, major_deficiency_comments: e.target.value }))}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
              rows={4}
            />
            {formData.major_deficiency_comments.trim() && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm font-medium">
                  ⚠️ Major deficiencies reported. An alert email will be automatically sent to {currentUser?.company?.email || 'the office'}
                </p>
              </div>
            )}
          </div>

          {/* Digital Signature */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Digital Signature</h2>
            <input
              type="text"
              placeholder="Inspector Digital Signature (Type Full Name)"
              value={formData.inspector_signature}
              onChange={(e) => setFormData(prev => ({ ...prev, inspector_signature: e.target.value }))}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 signature-field"
              required
            />
            <p className="text-gray-500 text-sm mt-2">
              By typing your name above, you certify this inspection was conducted in accordance with Ontario Regulation 199/07.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-4 rounded-xl font-bold text-xl transition-colors"
            >
              {isSubmitting ? 'Submitting Inspection...' : 'Submit Ontario Regulation 199/07 Inspection'}
            </button>
          </div>

          {/* Compliance Footer */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
            <p className="text-gray-600 text-sm">
              This inspection form complies with Ontario Regulation 199/07 (Schedule 1) for pickup trucks and tag-along trailers without air brakes.
              {formData.major_deficiency_comments.trim() !== '' && (
                <><br/><strong className="text-red-600">Alert emails will be sent to {currentUser?.company?.email || 'the office'} for any deficiencies reported.</strong></>
              )}
            </p>
          </div>

        </form>
        </div>
      </div>
  );
};

// Calendar Preview Component for Job Scheduling

export default VehicleInspections;
